import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './AlpacaBot.css';

const AlpacaBot = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [botRunning, setBotRunning] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const justStartedRef = useRef(false);
  const [logs, setLogs] = useState([]);
  const [positions, setPositions] = useState([]);
  const [positionHighlights, setPositionHighlights] = useState({}); // { symbol: 'profit' | 'loss' | null }
  const previousProfitsRef = useRef({}); // { symbol: profitLoss }
  const [configs, setConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const lastBotRunningStateRef = useRef(null); // Pour éviter les changements rapides
  const botRunningChangeCountRef = useRef(0); // Compteur de changements rapides

  const { token, logout } = useAuth();

  // Charger la liste des configurations disponibles
  useEffect(() => {
    const loadConfigs = async () => {
      try {
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const res = await fetch('/api/alpaca/config/all', { headers });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.data && data.data.length > 0) {
            setConfigs(data.data);
            // Sélectionner la première config par défaut seulement si aucune n'est sélectionnée
            if (!selectedConfigId && data.data[0]) {
              setSelectedConfigId(data.data[0].id);
            }
          }
        }
      } catch (e) {
        console.error('Erreur lors du chargement des configurations:', e);
      }
    };

    if (token) {
      loadConfigs();
    }
  }, [token]);
  
  // Réinitialiser les données quand on change de sous-compte
  useEffect(() => {
    if (!selectedConfigId) {
      return;
    }
    
    // Réinitialiser tous les états lors du changement de sous-compte
    setLoading(true);
    setError(null);
    setBotRunning(false);
    setStats(null);
    setPositions([]);
    setLogs([]);
    setPositionHighlights({});
    previousProfitsRef.current = {};
    justStartedRef.current = false;
    lastBotRunningStateRef.current = null;
    botRunningChangeCountRef.current = 0;
  }, [selectedConfigId]);

  const loadStats = async (configId = selectedConfigId) => {
    if (!configId) {
      return;
    }

    try {
      setError(null);
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const statsUrl = `/api/alpaca/bot/stats?config_id=${configId}`;
      const response = await fetch(statsUrl, { headers });

      if (response.status === 401) {
        let msg = 'Token invalide ou expiré';
        try {
          const j = await response.json();
          msg = j.message || msg;
        } catch {}
        setError(msg);
        await logout();
        return;
      }

      if (!response.ok) {
        let msg = 'Erreur lors du chargement des statistiques';
        try {
          const j = await response.json();
          msg = j.error || j.message || msg;
        } catch {}
        throw new Error(msg);
      }

      const data = await response.json();
      if (data.success && data.data) {
        setStats(data.data);
        // Ne pas écraser botRunning si on vient juste de démarrer le bot
        if (!justStartedRef.current) {
          const newBotRunning = data.data.bot_running || false;
          const lastState = lastBotRunningStateRef.current;
          
          // Si l'état change
          if (lastState !== newBotRunning) {
            // Si on a eu plusieurs changements rapides (plus de 2 en peu de temps), 
            // on stabilise en gardant l'état précédent pendant un moment
            if (botRunningChangeCountRef.current > 2) {
              // Ne pas mettre à jour immédiatement, garder l'état actuel
              // Le compteur sera réinitialisé et on acceptera le changement après
              return;
            }
            
            // Mettre à jour l'état
            botRunningChangeCountRef.current++;
            lastBotRunningStateRef.current = newBotRunning;
            setBotRunning(newBotRunning);
          } else {
            // Si l'état est stable (pas de changement), réinitialiser le compteur
            if (botRunningChangeCountRef.current > 0) {
              botRunningChangeCountRef.current = 0;
            }
          }
        }
      }
    } catch (e) {
      setError(e.message || 'Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async (configId = selectedConfigId) => {
    if (!configId) {
      return;
    }

    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const logsUrl = `/api/alpaca/bot/logs?limit=100&config_id=${configId}`;
      const response = await fetch(logsUrl, { headers });

      if (response.status === 401) {
        await logout();
        return;
      }

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setLogs(data.data);
        }
      } else {
        console.error('[AlpacaBot] Failed to load logs:', response.status, response.statusText);
      }
    } catch (e) {
      console.error('[AlpacaBot] Error loading logs:', e);
    }
  };

  const loadPositions = async (configId = selectedConfigId) => {
    if (!configId) {
      return;
    }

    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const positionsUrl = `/api/alpaca/bot/positions?config_id=${configId}`;
      const response = await fetch(positionsUrl, { headers });

      if (response.status === 401) {
        await logout();
        return;
      }

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          // FILTRER : Exclure les positions en attente (ne garder que les positions exécutées)
          const newPositions = data.data.filter(position => {
            // Exclure si isPending est true
            if (position.isPending === true) {
              return false;
            }
            // Toutes les autres positions sont exécutées
            return true;
          });
          
          // Détecter les changements de profit pour chaque position
          const newHighlights = {};
          newPositions.forEach(position => {
            const symbol = position.symbol;
            const currentProfit = position.profitLoss || 0;
            const previousProfit = previousProfitsRef.current[symbol];
            
            // Si on a une valeur précédente et que le profit a changé
            if (previousProfit !== undefined && previousProfit !== currentProfit) {
              if (currentProfit > previousProfit) {
                // Profit a augmenté -> fond vert
                newHighlights[symbol] = 'profit';
                // Retirer le highlight après 2 secondes
                setTimeout(() => {
                  setPositionHighlights(prev => {
                    const updated = { ...prev };
                    if (updated[symbol] === 'profit') {
                      delete updated[symbol];
                    }
                    return updated;
                  });
                }, 2000);
              } else if (currentProfit < previousProfit) {
                // Profit a baissé -> fond rouge
                newHighlights[symbol] = 'loss';
                // Retirer le highlight après 2 secondes
                setTimeout(() => {
                  setPositionHighlights(prev => {
                    const updated = { ...prev };
                    if (updated[symbol] === 'loss') {
                      delete updated[symbol];
                    }
                    return updated;
                  });
                }, 2000);
              }
            }
            
            // Mettre à jour la valeur précédente
            previousProfitsRef.current[symbol] = currentProfit;
          });
          
          // Mettre à jour les highlights
          if (Object.keys(newHighlights).length > 0) {
            setPositionHighlights(prev => ({ ...prev, ...newHighlights }));
          }
          
          setPositions(newPositions);
        }
      } else {
        console.error('[AlpacaBot] Failed to load positions:', response.status, response.statusText);
      }
    } catch (e) {
      console.error('[AlpacaBot] Error loading positions:', e);
    }
  };

  // Charger les données quand le sous-compte change ou au démarrage
  useEffect(() => {
    if (!selectedConfigId) {
      setLoading(false);
      return;
    }

    // Capturer le configId au moment de l'effet pour éviter les closures stale
    const configId = selectedConfigId;

    // Charger toutes les données immédiatement
    const loadAllData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadStats(configId),
          loadLogs(configId),
          loadPositions(configId)
        ]);
      } catch (e) {
        console.error('Erreur lors du chargement des données:', e);
      } finally {
        setLoading(false);
      }
    };

    loadAllData();

    // Rafraîchir les stats et logs toutes les 2 secondes
    const interval = setInterval(() => {
      loadStats(configId);
      loadLogs(configId);
    }, 2000);

    return () => clearInterval(interval);
  }, [token, selectedConfigId]);

  // Effet séparé pour mettre à jour les positions seulement si le bot est actif
  useEffect(() => {
    if (!selectedConfigId) {
      return;
    }
    
    if (botRunning) {
      const configId = selectedConfigId;
      loadPositions(configId);
      const positionsInterval = setInterval(() => {
        loadPositions(configId);
      }, 1000);
      return () => clearInterval(positionsInterval);
    }
    // Si le bot s'arrête, on ne fait rien (les positions restent affichées mais ne sont plus mises à jour)
  }, [botRunning, selectedConfigId]);

  const handleStartBot = async () => {
    if (!selectedConfigId) {
      setError('Veuillez sélectionner un sous-compte');
      return;
    }

    try {
      setIsStarting(true);
      setError(null);
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch('/api/alpaca/bot/start', {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ config_id: selectedConfigId })
      });

      if (response.status === 401) {
        await logout();
        return;
      }

      if (!response.ok) {
        let msg = 'Erreur lors du démarrage du bot';
        try {
          const j = await response.json();
          msg = j.error || j.message || msg;
        } catch {}
        throw new Error(msg);
      }

      const data = await response.json();
      if (data.success) {
        setBotRunning(true);
        justStartedRef.current = true;
        // Attendre un peu avant de recharger les stats pour laisser le bot s'initialiser
        setTimeout(async () => {
          justStartedRef.current = false;
          await loadStats();
        }, 3000);
      }
    } catch (e) {
      setError(e.message || 'Erreur de connexion au serveur');
    } finally {
      setIsStarting(false);
    }
  };

  const handleStopBot = async () => {
    if (!selectedConfigId) {
      setError('Veuillez sélectionner un sous-compte');
      return;
    }

    try {
      setIsStopping(true);
      setError(null);
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch('/api/alpaca/bot/stop', {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ config_id: selectedConfigId })
      });

      if (response.status === 401) {
        await logout();
        return;
      }

      if (!response.ok) {
        let msg = 'Erreur lors de l\'arrêt du bot';
        try {
          const j = await response.json();
          msg = j.error || j.message || msg;
        } catch {}
        throw new Error(msg);
      }

      const data = await response.json();
      if (data.success) {
        setBotRunning(false);
        await loadStats();
      }
    } catch (e) {
      setError(e.message || 'Erreur de connexion au serveur');
    } finally {
      setIsStopping(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return `$${parseFloat(value).toFixed(2)}`;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '0.00%';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${parseFloat(value).toFixed(2)}%`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('fr-FR');
    } catch {
      return dateStr;
    }
  };

  // Aucun bot configuré
  if (!loading && configs.length === 0) {
    return (
      <div className="alpaca-bot-page">
        <div className="alpaca-bot-header">
          <h1 className="alpaca-bot-title">🤖 Bot de Trading Alpaca</h1>
          <p className="alpaca-bot-subtitle">Surveillance automatique du portefeuille et trading</p>
        </div>
        <div style={{
          marginTop: '3rem',
          textAlign: 'center',
          padding: '3rem 2rem',
          background: '#f9fafb',
          border: '2px dashed #d1d5db',
          borderRadius: '12px',
          color: '#6b7280'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚙️</div>
          <h2 style={{ margin: '0 0 0.5rem', color: '#374151' }}>Aucun bot configuré</h2>
          <p style={{ margin: 0 }}>Rendez-vous dans <strong>Alpaca → Configuration</strong> pour créer votre première configuration de bot.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="alpaca-bot-page">
      <div className="alpaca-bot-header">
        <h1 className="alpaca-bot-title">🤖 Bot de Trading Alpaca</h1>
        <p className="alpaca-bot-subtitle">Surveillance automatique du portefeuille et trading</p>
        {configs.length > 0 && (
          <div className="bot-config-selector">
            <label htmlFor="bot-config-select">Sous-compte:</label>
            <select
              id="bot-config-select"
              value={selectedConfigId || ''}
              onChange={(e) => {
                const newConfigId = e.target.value ? parseInt(e.target.value) : null;
                if (newConfigId !== selectedConfigId) {
                  setSelectedConfigId(newConfigId);
                }
              }}
            >
              {configs.map((config) => (
                <option key={config.id} value={config.id}>
                  {config.sub_account_name || 'Sans nom'} ({config.account_type === 'live' ? 'Live' : 'Paper'})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Contrôles du bot */}
      <div className="alpaca-bot-controls">
        <div className="bot-status">
          <span className={`bot-status-indicator ${botRunning ? 'running' : 'stopped'}`}>
            {botRunning ? '🟢' : '🔴'}
          </span>
          <span className="bot-status-text">
            {botRunning ? 'Bot actif' : 'Bot arrêté'}
          </span>
          {selectedConfigId && configs.length > 0 && (() => {
            const currentConfig = configs.find(c => c.id === selectedConfigId);
            if (currentConfig) {
              const thresholdUp = currentConfig.sell_threshold_up || 10.00;
              const thresholdDown = currentConfig.sell_threshold_down || -5.00;
              return (
                <span className="bot-thresholds" style={{ 
                  marginLeft: '1.5rem', 
                  fontSize: '0.95rem', 
                  color: '#374151',
                  fontWeight: '500',
                  padding: '0.5rem 1rem',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  📊 Règles de vente: <span style={{ color: '#10b981', fontWeight: '600' }}>+{thresholdUp.toFixed(2)}%</span> / <span style={{ color: '#ef4444', fontWeight: '600' }}>{thresholdDown.toFixed(2)}%</span>
                </span>
              );
            }
            return null;
          })()}
        </div>
        <div className="bot-actions">
          {!botRunning ? (
            <button
              className="bot-button start-button"
              onClick={handleStartBot}
              disabled={isStarting}
            >
              {isStarting ? '⏳ Démarrage...' : '▶️ Démarrer le bot'}
            </button>
          ) : (
            <button
              className="bot-button stop-button"
              onClick={handleStopBot}
              disabled={isStopping}
            >
              {isStopping ? '⏳ Arrêt...' : '⏹️ Arrêter le bot'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement des statistiques...</p>
        </div>
      ) : stats ? (
        <>
          {/* Statistiques principales */}
          <div className="bot-stats-grid">
            <div className="stat-card">
              <div className="stat-label">Total Transactions</div>
              <div className="stat-value">{stats.total_transactions || 0}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Achats</div>
              <div className="stat-value buy">{stats.total_buys || 0}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Ventes</div>
              <div className="stat-value sell">{stats.total_sells || 0}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Profit/Perte réalisé</div>
              <div className={`stat-value ${(stats.realized_pl || 0) >= 0 ? 'profit' : 'loss'}`}>
                {formatCurrency(stats.realized_pl)}
              </div>
              <div className="stat-sublabel">Trades clôturés</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Positions ouvertes</div>
              <div className={`stat-value ${(stats.unrealized_pl || 0) >= 0 ? 'profit' : 'loss'}`}>
                {formatCurrency(stats.unrealized_pl)}
              </div>
              <div className="stat-sublabel">Non réalisé (prix actuel)</div>
            </div>
            <div className="stat-card stat-card-total">
              <div className="stat-label">P&amp;L Total</div>
              <div className={`stat-value stat-value-lg ${(stats.total_profit_loss || 0) >= 0 ? 'profit' : 'loss'}`}>
                {formatCurrency(stats.total_profit_loss)}
              </div>
              <div className="stat-sublabel">Réalisé + non réalisé</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Total Investi</div>
              <div className="stat-value">{formatCurrency(stats.total_invested)}</div>
            </div>
          </div>

          {/* Positions actives en temps réel */}
          <div className="bot-positions">
            <h2 className="section-title">
              📊 Positions Actives {botRunning ? '(Temps Réel)' : '(Statique - Bot arrêté)'}
            </h2>
            {!botRunning && (
              <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', color: '#856404' }}>
                <strong>⚠️ Bot arrêté :</strong> Les positions affichées ne sont plus mises à jour en temps réel. Démarrez le bot pour activer la surveillance en temps réel.
              </div>
            )}
            <div className="positions-grid">
              {positions && positions.length > 0 ? (
                positions.slice(0, 5).map((position, index) => {
                  const highlightClass = positionHighlights[position.symbol] || '';
                  return (
                  <div key={position.symbol} className={`position-card ${highlightClass}`}>
                    <div className="position-header">
                      <div className="position-symbol">{position.symbol}</div>
                      {position.isPending && (
                        <span className="position-pending-badge">⏳ En attente (Ordre non exécuté)</span>
                      )}
                    </div>
                    <div className="position-details">
                      <div className="position-row">
                        <span className="position-label">Quantité:</span>
                        <span className="position-value">{parseFloat(position.quantity).toFixed(4)}</span>
                      </div>
                      <div className="position-row">
                        <span className="position-label">{position.isPending ? 'Prix de l\'ordre:' : 'Prix d\'entrée:'}</span>
                        <span className="position-value">{formatCurrency(position.avgEntryPrice)}</span>
                      </div>
                      {!position.isPending && (
                        <div className="position-row">
                          <span className="position-label">Prix actuel:</span>
                          <span className="position-value position-current-price">{formatCurrency(position.currentPrice)}</span>
                        </div>
                      )}
                      {position.isPending && (
                        <>
                          <div className="position-row" style={{marginBottom: '10px'}}>
                            <span className="position-label" style={{fontStyle: 'italic', color: '#856404', fontWeight: 'bold'}}>
                              ⚠️ Ordre en attente chez Alpaca
                            </span>
                          </div>
                          {position.orderType && (
                            <div className="position-row">
                              <span className="position-label" style={{fontSize: '0.85em', color: '#666'}}>
                                Type: <strong>{position.orderType}</strong> | Statut Alpaca: <strong>{position.orderStatus}</strong>
                              </span>
                            </div>
                          )}
                          {position.avgEntryPrice && position.avgEntryPrice > 0 ? (
                            <div className="position-row">
                              <span className="position-label">Prix limite:</span>
                              <span className="position-value">{formatCurrency(position.avgEntryPrice)}</span>
                            </div>
                          ) : (
                            <div className="position-row">
                              <span className="position-label" style={{fontSize: '0.85em', color: '#666', fontStyle: 'italic'}}>
                                Ordre market - Prix d'exécution au prix du marché (non affiché tant que non exécuté)
                              </span>
                            </div>
                          )}
                          {position.reasons && position.reasons.length > 0 && (
                            <div className="position-row" style={{marginTop: '10px', padding: '8px', backgroundColor: '#fff3cd', borderRadius: '4px', fontSize: '0.85em'}}>
                              <span style={{color: '#856404'}}>
                                <strong>Raisons de l'attente (Alpaca):</strong> {position.reasons.join(', ')}
                              </span>
                            </div>
                          )}
                          {position.submittedAt && (
                            <div className="position-row" style={{fontSize: '0.85em', color: '#666', marginTop: '5px'}}>
                              <span className="position-label">Ordre créé le:</span>
                              <span className="position-value">{new Date(position.submittedAt).toLocaleString('fr-FR')}</span>
                            </div>
                          )}
                          {position.currentMarketPrice && (
                            <div className="position-row">
                              <span className="position-label" style={{fontSize: '0.85em', color: '#888'}}>
                                Prix marché actuel: {formatCurrency(position.currentMarketPrice)}
                              </span>
                            </div>
                          )}
                          {position.pendingReason && (
                            <div className="position-row" style={{marginTop: '8px', padding: '8px', backgroundColor: '#fff3cd', borderRadius: '4px'}}>
                              <span className="position-label" style={{fontSize: '0.85em', color: '#856404'}}>
                                ℹ️ {position.pendingReason}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                      <div className="position-row">
                        <span className="position-label">Valeur marché:</span>
                        <span className="position-value">{formatCurrency(position.marketValue)}</span>
                      </div>
                      <div className="position-row position-profit-loss">
                        <span className="position-label">Profit/Perte:</span>
                        <span className={`position-value ${position.profitLoss >= 0 ? 'profit' : 'loss'}`}>
                          {formatCurrency(position.profitLoss)} ({position.profitLossPercent >= 0 ? '+' : ''}{position.profitLossPercent.toFixed(2)}%)
                        </span>
                      </div>
                    </div>
                  </div>
                  );
                })
              ) : (
                <div className="empty-positions">
                  <p>Aucune position active</p>
                </div>
              )}
              {/* Afficher des cartes vides si moins de 5 positions */}
              {positions && positions.length < 5 && Array.from({ length: 5 - positions.length }).map((_, index) => (
                <div key={`empty-${index}`} className="position-card position-card-empty">
                  <div className="position-header">
                    <div className="position-symbol">-</div>
                  </div>
                  <div className="position-details">
                    <div className="position-empty-text">Position vide</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Transactions récentes */}
          <div className="bot-transactions">
            <h2 className="section-title">📋 Transactions Récentes</h2>
            {stats.recent_transactions && stats.recent_transactions.length > 0 ? (
              <div className="transactions-table-container">
                <table className="transactions-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Symbole</th>
                      <th>Action</th>
                      <th>Quantité</th>
                      <th>Prix</th>
                      <th>Montant</th>
                      <th>Raison</th>
                      <th>Profit/Perte</th>
                      <th>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_transactions.map((transaction, index) => (
                      <tr key={transaction.alpaca_order_id || transaction.id || index}>
                        <td>{formatDate(transaction.created_at)}</td>
                        <td><strong>{transaction.symbol}</strong></td>
                        <td>
                          <span className={`action-badge ${transaction.action}`}>
                            {transaction.action === 'buy' ? '📈 Achat' : '📉 Vente'}
                          </span>
                        </td>
                        <td>{parseFloat(transaction.quantity).toFixed(4)}</td>
                        <td>{formatCurrency(transaction.price)}</td>
                        <td>{formatCurrency(transaction.total_amount)}</td>
                        <td>{transaction.reason || '-'}</td>
                        <td>
                          {transaction.profit_loss !== null ? (
                            <span className={transaction.profit_loss >= 0 ? 'profit' : 'loss'}>
                              {formatCurrency(transaction.profit_loss)}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          <span className={`status-badge status-${transaction.status || 'pending'}`}>
                            {transaction.status === 'filled' || transaction.status === 'partially_filled' ? '✅ Rempli' : 
                             transaction.status === 'new' || transaction.status === 'pending' || transaction.status === 'pending_new' || transaction.status === 'accepted' || transaction.status === 'pending_replace' ? '⏳ En attente' : 
                             transaction.status === 'canceled' || transaction.status === 'expired' || transaction.status === 'rejected' ? '❌ Échoué' :
                             '⏳ ' + (transaction.status || 'En attente')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-message">
                <p>Aucune transaction enregistrée.</p>
              </div>
            )}
          </div>

          {/* Logs en temps réel */}
          <div className="bot-logs">
            <h2 className="section-title">📝 Logs en temps réel</h2>
            <div className="logs-container">
              {logs.length > 0 ? (
                logs.map((log, index) => (
                  <div key={index} className={`log-entry log-${log.level}`}>
                    <span className="log-timestamp">{formatDate(log.timestamp)}</span>
                    <span className={`log-level log-level-${log.level}`}>
                      {log.level === 'info' && 'ℹ️'}
                      {log.level === 'success' && '✅'}
                      {log.level === 'warning' && '⚠️'}
                      {log.level === 'error' && '❌'}
                    </span>
                    <span className="log-message">{log.message}</span>
                    {log.data && (
                      <span className="log-data">{JSON.stringify(log.data)}</span>
                    )}
                  </div>
                ))
              ) : (
                <div className="empty-message">
                  <p>Aucun log disponible.</p>
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="empty-message">
          <p>Aucune statistique disponible.</p>
        </div>
      )}
    </div>
  );
};

export default AlpacaBot;

